/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const Modal = ({ isOpen, onClose, title, children, className = '', fontFamily, theme, closeOnBackdropClick = true }) => {
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      // Handle escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);

      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) { return null; }

  // A custom className only overrides the sizing/scroll aspects it actually
  // specifies; anything it doesn't set (width, max-height, overflow) falls
  // back to the default so callers can't silently lose scroll/viewport bounds.
  const hasWidthClassName = /\b(?:w-|max-w-)\S+/.test(className);
  const hasMaxHeightClassName = /\bmax-h-\S+/.test(className);
  const hasOverflowClassName = /\boverflow(?:-[xy])?-\S+/.test(className);
  const sizeDefaults = [
    !hasWidthClassName && 'max-w-md w-full',
    !hasMaxHeightClassName && 'max-h-[90vh]',
  ].filter(Boolean).join(' ');
  const overflowClassName = hasOverflowClassName ? '' : 'overflow-y-auto no-scrollbar';

  return createPortal(
    <div
      data-theme={theme}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        fontFamily: fontFamily || undefined
      }}
      onClick={(e) => {
        // Close modal when clicking backdrop (unless the caller opted out)
        if (closeOnBackdropClick && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`bg-base-100 rounded-2xl shadow-2xl ${className} ${sizeDefaults} ${overflowClassName}`.replace(/\s+/g, ' ').trim()}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-base-content/10">
            <h3 className="text-lg font-bold text-base-content">{title}</h3>
            <button
              onClick={onClose}
              className="p-1 text-base-content/40 hover:text-base-content rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        )}
        <div className={title ? 'p-6' : 'p-6'}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;

Modal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  theme: PropTypes.string,
  closeOnBackdropClick: PropTypes.bool,
};